import { cn } from '../lib/cn';
import { badgeVariants } from './badge';

export type MotifChipItem = { nom: string; couleur: string | null };

/**
 * Teinte que le cabinet a donnée au motif dans son planning Doctolib.
 *
 * Décorative : ces couleurs sont choisies pour des blocs d'agenda, sans égard
 * au contraste. D'où la pastille cerclée, qui détoure les teintes claires, et
 * le nom qui reste seul porteur de l'information.
 */
function Pastille({ couleur }: { couleur: string | null }) {
  if (!couleur) return <span aria-hidden="true" className="size-2.5 shrink-0" />;

  return (
    <span
      aria-hidden="true"
      style={{ backgroundColor: couleur }}
      className="size-2.5 shrink-0 rounded-full ring-1 ring-inset ring-content/15"
    />
  );
}

/**
 * « 24 motifs », avec la liste complète au survol.
 *
 * Le compte plutôt que les noms : ceux-ci peuvent être nombreux, et la ligne
 * n'a pas à s'étirer pour les tenir. Un panneau à soi, affiché au survol du
 * groupe, plutôt que `title` : dans un `<button>`, certains navigateurs
 * retardent ou avalent l'infobulle native.
 */
export function MotifsChip({
  motifs,
  logo,
  align = 'end',
  appearance = 'pill',
  className,
}: {
  motifs: readonly MotifChipItem[];
  /** Logotype de la source des motifs (Doctolib), en tête de l'infobulle. */
  logo?: string;
  /** Côté de la pastille où l'infobulle s'ancre : `end` s'étend vers la gauche. */
  align?: 'start' | 'end';
  /** `badge` : le gabarit des autres pastilles (`Badge`). `link` : texte souligné d'un lien, pour un
   * compte glissé dans une phrase. */
  appearance?: 'pill' | 'badge' | 'link';
  className?: string;
}) {
  if (motifs.length === 0) return null;

  return (
    <span
      tabIndex={0}
      className={cn(
        'group relative flex shrink-0 items-center gap-1.5 outline-none',
        appearance === 'badge' && badgeVariants({ tone: 'neutral', size: 'sm' }),
        appearance === 'link' &&
          'cursor-help font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700',
        appearance === 'pill' &&
          'rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-content-muted',
        className,
      )}
    >
      {appearance === 'link' ? null : (
        <span aria-hidden="true" className="flex -space-x-1">
          {motifs.slice(0, 3).map((motif, index) => (
            <Pastille key={`${motif.nom}-${index}`} couleur={motif.couleur} />
          ))}
        </span>
      )}
      {motifs.length} motif{motifs.length > 1 ? 's' : ''}
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none absolute top-full z-20 mt-2 hidden w-max max-w-56 rounded-lg bg-content px-3 py-2 text-left text-xs font-normal text-surface shadow-card group-focus-within:block group-hover:block',
          align === 'end' ? 'right-0' : 'left-0',
        )}
      >
        {/* La source du nom, avant la liste : ces motifs sont ceux du planning
            Doctolib, pas une catégorie de l'application — le logotype le dit
            plus vite qu'une phrase. */}
        <span className="mb-1.5 flex items-center gap-1.5 border-b border-white/15 pb-1.5 font-medium text-white/70">
          {logo ? <img src={logo} alt="" aria-hidden="true" className="h-3 w-auto" /> : null}
          Motifs Doctolib
        </span>

        <span className="flex flex-col gap-1">
          {motifs.map((motif, index) => (
            <span key={`${motif.nom}-${index}`} className="flex items-center gap-2">
              {/* Le liseré est clair et non sombre : sur un fond navy, le
                  cerclage utilisé ailleurs s'y fondait. */}
              <span
                style={{ backgroundColor: motif.couleur ?? undefined }}
                className="size-2 shrink-0 rounded-full ring-1 ring-inset ring-white/30"
              />
              <span className="truncate">{motif.nom}</span>
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}
